import { useState } from "react";
import { format } from "date-fns";
import api, { errorMessage } from "./api";
import useAdminStore from "./adminStore";
import { filterOrders, ORDER_FILTERS, ORDER_STATUS, orderStatus } from "./adminLogic";
import { Card, ChoiceButtons, ConfirmButton, Empty, Hint, PageTitle } from "./ui";
import { euro } from "../components/order/orderUi";

const OrderCard = ({ order }) => {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(null);
    const removeOrder = useAdminStore((state) => state.removeOrder);
    const f = order.formData || {};
    const status = ORDER_STATUS[orderStatus(order)];
    const created = new Date(order.createdAt);

    const remove = async () => {
        try {
            await api.delete(`/orders/${order._id}`);
            removeOrder(order._id);
        } catch (err) {
            setError(errorMessage(err, "Löschen fehlgeschlagen."));
        }
    };

    return (
        <Card>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-caslon text-xl">#{order.orderNumber}</div>
                    <div className="mt-0.5 text-sm">
                        {f.firstName} {f.lastName} · {order.pickup ? "Abholung" : "Lieferung"}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[17px] tabular-nums">{euro(order.totalPrice)}</div>
                    <div className="text-xs text-forest/55">{format(created, "dd.MM. · HH:mm")} Uhr</div>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`border px-2.5 py-1 text-xs font-semibold uppercase tracking-[.1em] ${status.className}`}>
                    {status.label}
                </span>
                {order.printed && <span className="text-xs text-forest/55">gedruckt</span>}
            </div>
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="mt-3 block bg-transparent py-1.5 text-[13px] underline"
            >
                {open ? "Details ausblenden" : "Details anzeigen"}
            </button>
            {open && (
                <div className="mt-2 grid gap-2 border-t border-forest/10 pt-2.5 text-sm">
                    {(order.cart || []).map((item, index) => (
                        <div key={index} className="flex justify-between gap-3">
                            <span>
                                {item.quantity}× {item.title}
                                <span className="text-forest/55">
                                    {" "}
                                    {[item.beilage && `mit ${item.beilage}`, item.customerComment]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </span>
                            </span>
                            <span className="tabular-nums">{euro(item.price * item.quantity)}</span>
                        </div>
                    ))}
                    <div className="text-[13px] text-forest/60">
                        <a href={`tel:${f.phoneNumber}`} className="underline">
                            {f.phoneNumber}
                        </a>{" "}
                        ·{" "}
                        <a href={`mailto:${f.email}`} className="underline">
                            {f.email}
                        </a>
                    </div>
                    {!order.pickup && (
                        <div className="text-[13px] text-forest/60">
                            {f.street}, {f.zipCode} {f.city}
                        </div>
                    )}
                    <ConfirmButton onConfirm={remove} className="mt-1.5 justify-self-start">
                        Bestellung löschen
                    </ConfirmButton>
                    {error && <div className="text-sm text-fehler">{error}</div>}
                </div>
            )}
        </Card>
    );
};

export default function OrdersPage() {
    const [filter, setFilter] = useState("offen");
    const { orders, ordersLoaded } = useAdminStore();
    const visible = filterOrders(orders, filter);

    return (
        <div>
            <PageTitle aside={ordersLoaded ? `${orders.length} insgesamt` : null}>Bestellungen</PageTitle>
            <ChoiceButtons label="Status" options={ORDER_FILTERS} value={filter} onChange={setFilter} />
            <Hint className="mb-3.5">
                Annahme und Ablehnung erfolgen am Bestell-Terminal in der Küche. Hier nur Übersicht und Löschen.
            </Hint>
            <div className="grid gap-2.5">
                {visible.map((order) => (
                    <OrderCard key={order._id} order={order} />
                ))}
                {ordersLoaded && visible.length === 0 && <Empty>Keine Bestellungen in dieser Ansicht.</Empty>}
            </div>
        </div>
    );
}
